import { useState } from "react";
import { BUTTON } from "@/lib/buttonStyles";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useLocation, Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
const HERO_BG = "/assets/hero-bg-Y8F6k5ayA9btVQcqwjUfpV.webp";

export default function ForgotPassword() {
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const requestResetMutation = trpc.auth.requestPasswordReset.useMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setIsLoading(true);
    try {
      await requestResetMutation.mutateAsync({ email });
      setSubmitted(true);
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    /* flex column with a flex-1 section, so the footer is pinned to the
       bottom of the viewport rather than floating halfway up a short page. */
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader showPrimaryAction={false} />

      {/* ── HERO SECTION ── */}
      <section className="flex-1 relative overflow-hidden min-h-[calc(100vh-3.5rem)] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-background/55" />

        <div className="relative container py-16">
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-card rounded-lg border border-border shadow-[0_2px_8px_oklch(0.22_0.015_65/0.06)] p-8">
                {!submitted ? (
                  <>
                    <h1 className="font-['Fraunces'] text-2xl font-bold text-foreground mb-1">
                      Reset Your Password
                    </h1>
                    <p className="text-sm text-muted-foreground mb-6">
                      Enter your email address and we'll send you a link to reset your password.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                          Email Address
                        </label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={isLoading}
                          className="w-full bg-background"
                          autoFocus
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full ${BUTTON.primary}`}
                      >
                        {isLoading ? "Resetting..." : (
                          <>
                            Reset Password
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </form>

                    <p className="text-sm text-muted-foreground text-center mt-6">
                      Remember your password?{" "}
                      <button
                        onClick={() => setLocation("/auth/login")}
                        className="text-primary hover:underline font-semibold"
                      >
                        Sign in
                      </button>
                    </p>
                  </>
                ) : (
                  /* ── SUCCESS STATE ── */
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="text-center"
                  >
                    <div className="flex justify-center mb-4">
                      <CheckCircle className="w-12 h-12 text-[oklch(0.38_0.09_155)]" />
                    </div>
                    <h2 className="font-['Fraunces'] text-xl font-bold text-foreground mb-2">
                      Check your inbox
                    </h2>
                    <p className="text-sm text-muted-foreground mb-6">
                      If an account exists for <strong>{email}</strong>, we have emailed a link for setting a new password. It works for the next 15 minutes.
                    </p>

                    <button
                      onClick={() => setLocation("/auth/login")}
                      className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to sign in
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
